import React, { useState } from 'react';
import {
  CONDITION_OPTIONS,
  FOIL_OPTIONS,
  LANGUAGE_COLLAPSE_AT,
  LANGUAGE_OPTIONS,
  PrintingsFilterState,
} from './printingsFilters';

interface FiltersPopoverProps {
  state: PrintingsFilterState;
  onChange: (patch: Partial<PrintingsFilterState>) => void;
  onReset: () => void;
  onDone: () => void;
  /** Touch sizing for the mobile sheet (48px chips instead of 9px-radius desktop chips). */
  touch?: boolean;
}

const toggle = (list: string[], value: string) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

/**
 * The Finish / Language / Condition body, shared by the desktop popover and the
 * mobile sheet so the two cannot drift. Presentation only: the header owns the
 * state and forwards the complete object through onFiltersChange.
 */
const FiltersPopover: React.FC<FiltersPopoverProps> = ({
  state,
  onChange,
  onReset,
  onDone,
  touch = false,
}) => {
  const [showAllLanguages, setShowAllLanguages] = useState(false);
  const hiddenLanguages = LANGUAGE_OPTIONS.length - LANGUAGE_COLLAPSE_AT;
  const visibleLanguages = showAllLanguages
    ? LANGUAGE_OPTIONS
    : LANGUAGE_OPTIONS.slice(0, LANGUAGE_COLLAPSE_AT);

  const chip = (label: string, selected: boolean, onClick: () => void) => (
    <button
      key={label}
      type="button"
      className={`cp-chip ${selected ? 'selected' : ''}`}
      aria-pressed={selected}
      onClick={onClick}
    >
      {label}
    </button>
  );

  return (
    <div className={`cp-filters-body ${touch ? 'touch' : ''}`}>
      <section className="cp-filters-section">
        <h4 className="cp-filters-label">Finish</h4>
        <div className="cp-chip-row">
          {FOIL_OPTIONS.map((opt) =>
            chip(opt, state.foil.includes(opt), () => onChange({ foil: toggle(state.foil, opt) })),
          )}
        </div>
      </section>

      <section className="cp-filters-section">
        <h4 className="cp-filters-label">Language</h4>
        <div className="cp-chip-row">
          {visibleLanguages.map((opt) =>
            chip(opt.label, state.languages.includes(opt.value), () =>
              onChange({ languages: toggle(state.languages, opt.value) }),
            ),
          )}
          {hiddenLanguages > 0 && (
            <button
              type="button"
              className="cp-chip-expander"
              onClick={() => setShowAllLanguages((v) => !v)}
            >
              {showAllLanguages ? 'Show less' : `+${hiddenLanguages} more`}
            </button>
          )}
        </div>
      </section>

      <section className="cp-filters-section">
        <h4 className="cp-filters-label">Condition</h4>
        <div className="cp-chip-row">
          {CONDITION_OPTIONS.map((opt) =>
            chip(opt, state.conditions.includes(opt), () =>
              onChange({ conditions: toggle(state.conditions, opt) }),
            ),
          )}
        </div>
      </section>

      {!touch && (
        <div className="cp-filters-footer">
          <button type="button" className="cp-filters-reset" onClick={onReset}>
            Reset filters
          </button>
          <button type="button" className="cp-filters-done" onClick={onDone}>
            Done
          </button>
        </div>
      )}
    </div>
  );
};

export default FiltersPopover;
